import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Password from '../components/signin/Password';
import { clearError, updatePassword } from '../actions/password';
import { sessionNew } from '../actions/session';
import memoize from '../utils/memoize';
import { checkPassword, rules, validatePassword } from '../utils/checkPassword';
import { getReactEnv } from '../constants';

export class SigninSecurityPassword extends Component {
  static propTypes = {
    actionClearError: PropTypes.func.isRequired,
    actionSessionNew: PropTypes.func.isRequired,
    actionUpdatePassword: PropTypes.func.isRequired,
    email: PropTypes.string,
    fname: PropTypes.string,
    lname: PropTypes.string,
    error: PropTypes.arrayOf(PropTypes.string),
    instruction: PropTypes.string.isRequired,
    nistPasswordV2: PropTypes.bool,
    reauthenticationRequired: PropTypes.bool,
  };

  static defaultProps = {
    email: '',
    fname: '',
    lname: '',
    error: [],
    nistPasswordV2: false,
    reauthenticationRequired: false,
  };

  state = {
    confirmation: '',
    current: '',
    newPass: '',
  };

  timer = null;

  componentDidMount() {
    if (getReactEnv() === 'production') {
      ReactGA.pageview(window.location.pathname);
    }
  }

  componentWillUnmount() {
    const { actionClearError } = this.props;
    this.saveRequest?.abort();
    actionClearError();

    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  // Memoize on primitive args so reference equality on the user-context
  // object doesn't bust the cache between renders.
  validate = memoize((password, email, fname, lname) =>
    validatePassword(password, { email, fname, lname }),
  );

  validateLegacy = memoize(checkPassword);

  checkSaveDisabled = memoize(
    (current, newPass, confirmation) =>
      !(
        current &&
        current.length &&
        newPass &&
        newPass.length &&
        confirmation &&
        confirmation.length
      ) || newPass !== confirmation,
  );

  handleCancel = () => {
    const { actionClearError } = this.props;
    this.setState(
      {
        confirmation: '',
        current: '',
        newPass: '',
      },
      actionClearError,
    );
  };

  handleConfirmIdentity = () => {
    const { actionSessionNew } = this.props;
    actionSessionNew();
  };

  handleCurrent = (e) => {
    const { value } = e.target;
    this.setState({
      current: value,
    });
  };

  handleNew1 = (e) => {
    const { value } = e.target;
    this.setState({
      newPass: value,
    });
  };

  handleNew2 = (e) => {
    const { value } = e.target;
    this.setState({
      confirmation: value,
    });
  };

  handleSave = async (e) => {
    e.preventDefault();

    const { email, actionUpdatePassword } = this.props;
    const { current, newPass, confirmation } = this.state;

    this.saveRequest = new AbortController();
    const { signal } = this.saveRequest;
    const success = await actionUpdatePassword(
      {
        confirmation,
        current,
        password: newPass,
        email,
      },
      { signal },
    );
    if (signal.aborted) {
      return;
    }

    this.setState({
      current: '',
      success,
    });

    if (success) {
      this.setState({
        confirmation: '',
        newPass: '',
      });

      this.timer = window.setTimeout(() => {
        this.setState({ success: false });
        this.timer = null;
      }, 3000);
    }
  };

  render() {
    const {
      email,
      fname,
      lname,
      error,
      instruction,
      isFetching,
      nistPasswordV2,
      reauthenticationRequired,
    } = this.props;
    const { confirmation, current, newPass, success } = this.state;

    const validation = nistPasswordV2
      ? this.validate(newPass, email, fname, lname)
      : null;
    const validated = nistPasswordV2 ? [] : this.validateLegacy(newPass);
    const invalid = nistPasswordV2
      ? newPass.length > 0 && !validation.isValid
      : !!newPass.length && !validated.every(Boolean);

    const isSaveDisabled =
      invalid ||
      isFetching ||
      this.checkSaveDisabled(current, newPass, confirmation);

    return (
      <Password
        confirmation={confirmation}
        current={current}
        error={error}
        email={email}
        handleCancel={this.handleCancel}
        handleConfirmIdentity={this.handleConfirmIdentity}
        handleCurrent={this.handleCurrent}
        handleNew1={this.handleNew1}
        handleNew2={this.handleNew2}
        handleSave={this.handleSave}
        isSaveDisabled={isSaveDisabled}
        instruction={instruction}
        newPass={newPass}
        invalid={invalid}
        nistPasswordV2={nistPasswordV2}
        reauthenticationRequired={reauthenticationRequired}
        rules={rules}
        validated={validated}
        validationErrors={nistPasswordV2 ? validation.errors : []}
        success={success}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  ...state.password,
  email: state.user.email,
  fname: state.user.fname,
  lname: state.user.lname,
  nistPasswordV2: !!state.featureFlags?.nistPasswordV2,
});

const mapDispatchToProps = {
  actionClearError: clearError,
  actionSessionNew: sessionNew,
  actionUpdatePassword: updatePassword,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SigninSecurityPassword),
);
